
import { useState, useEffect, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Clock } from "lucide-react";
import { formatTimeRemaining } from "@/lib/utils";
import { useOnboarding } from "@/hooks/useOnboarding";

interface TimerButtonProps {
  children: ReactNode;
  onClick: () => void;
  timerSeconds: number;
  disabled?: boolean;
  className?: string;
}

export function TimerButton({
  children,
  onClick,
  timerSeconds,
  disabled = false,
  className,
}: TimerButtonProps) {
  const { isAdmin } = useOnboarding();
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  
  // Generate a globally unique ID for this timer instance
  const getTimerId = () => {
    const path = window.location.pathname;
    // Include component children as part of the ID to make it more unique
    const contentHash = String(children).slice(0, 20).replace(/\W/g, '');
    return `timer_${path}_${timerSeconds}_${contentHash}`;
  };
  
  const timerId = getTimerId();
  
  useEffect(() => {
    // Check for admin mode first - if admin, set timer to 0
    if (isAdmin) {
      console.log("Admin mode detected, skipping timer");
      setTimeRemaining(0);
      setTimerActive(false);
      return () => {};
    }
    
    // Get the current timestamp
    const now = Date.now();
    let endTime = 0;
    let remainingTime = 0;
    let storedData = null;
    
    try {
      // Check if there's a stored timer for this component
      storedData = localStorage.getItem(timerId);
      
      if (storedData) {
        try {
          // Parse the stored data
          const data = JSON.parse(storedData);
          endTime = parseInt(data.endTime);
          
          if (isNaN(endTime)) {
            throw new Error("Invalid end time");
          }
          
          // Calculate remaining time
          remainingTime = Math.max(0, Math.floor((endTime - now) / 1000));
          
          if (remainingTime > 0) {
            // Timer is still active
            console.log(`Timer ${timerId} is active with ${remainingTime} seconds remaining`);
            setTimeRemaining(remainingTime);
            setTimerActive(true);
          } else {
            // Timer has expired
            console.log(`Timer ${timerId} has expired`);
            setTimeRemaining(0);
            setTimerActive(false);
            localStorage.removeItem(timerId);
          }
        } catch (parseError) {
          console.error("Error parsing timer data:", parseError);
          // Reset timer if there's an error
          endTime = now + (timerSeconds * 1000);
          remainingTime = timerSeconds;
          setTimeRemaining(remainingTime);
          setTimerActive(true);
          try {
            localStorage.setItem(timerId, JSON.stringify({ endTime }));
          } catch (storageError) {
            console.warn("Failed to write to localStorage:", storageError);
          }
        }
      } else {
        // No stored timer, initialize a new one
        endTime = now + (timerSeconds * 1000);
        remainingTime = timerSeconds;
        console.log(`Initializing new timer ${timerId} with ${remainingTime} seconds`);
        setTimeRemaining(remainingTime);
        setTimerActive(true);
        
        // Store the end time in localStorage with additional metadata
        try {
          localStorage.setItem(timerId, JSON.stringify({ 
            endTime,
            createdAt: now,
            totalDuration: timerSeconds
          }));
        } catch (storageError) {
          console.warn("Failed to write to localStorage:", storageError);
        }
      }
    } catch (error) {
      console.error("Error accessing timer storage:", error);
      // Fallback to in-memory timer
      remainingTime = timerSeconds;
      setTimeRemaining(remainingTime);
      setTimerActive(true);
    }
    
    // Update the timer every second
    const interval = setInterval(() => {
      setTimeRemaining((prevTime) => {
        // Admin mode speeds up the timer by 1000x (super quick)
        const decrement = isAdmin ? 1000 : 1;
        const newTime = Math.max(0, prevTime - decrement);
        
        if (newTime <= 0) {
          clearInterval(interval);
          setTimerActive(false);
          try {
            localStorage.removeItem(timerId);
          } catch (error) {
            console.warn("Failed to remove timer from localStorage:", error);
          }
          return 0;
        }
        
        return newTime;
      });
    }, 1000);
    
    // Sync timer to localStorage every 15 seconds to ensure persistence
    const syncInterval = setInterval(() => {
      if (timerActive) {
        const currentEndTime = now + (timeRemaining * 1000);
        try {
          localStorage.setItem(timerId, JSON.stringify({ 
            endTime: currentEndTime,
            createdAt: now,
            totalDuration: timerSeconds
          }));
        } catch (error) {
          console.warn("Failed to sync timer to localStorage:", error);
        }
      }
    }, 15000);
    
    return () => {
      clearInterval(interval);
      clearInterval(syncInterval);
      // Don't remove from localStorage so timer persists when component unmounts
    };
  }, [timerSeconds, timerId, isAdmin]);

  // Event handling for visibility changes - update timer when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page is now visible, recalculate the remaining time
        try {
          const storedData = localStorage.getItem(timerId);
          if (storedData) {
            const { endTime } = JSON.parse(storedData);
            const now = Date.now();
            const remainingTime = Math.max(0, Math.floor((endTime - now) / 1000));
            
            if (remainingTime > 0) {
              setTimeRemaining(remainingTime);
              setTimerActive(true);
            } else {
              setTimeRemaining(0);
              setTimerActive(false);
              localStorage.removeItem(timerId);
            }
          }
        } catch (error) {
          console.warn("Error updating timer on visibility change:", error);
        }
      }
    };
    
    // Listen for visibility change events
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [timerId]);

  return (
    <Button
      onClick={onClick}
      disabled={disabled || timeRemaining > 0}
      className={cn("relative", className)}
      size="lg"
    >
      <span>{children}</span>
      
      {timeRemaining > 0 && (
        <div className="absolute inset-0 flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground">
          <Clock className="h-4 w-4 animate-pulse" />
          <span>{formatTimeRemaining(timeRemaining)}</span>
        </div>
      )}
    </Button>
  );
}
